import styled from '@emotion/styled';
import { Text, tokens } from '@hotjar/hotsauce';
import { useTranslationWithPrefix } from '@hotjar/translations';
import type { Currency } from '~/subscription-management/api/common/model/customer';
import type {
  BillingPeriod,
  ProductAllowance,
} from '~/subscription-management/api/common/model/subscription';
import type { OrganizationListItem } from '../../hooks/useServiceResourceList';
import type { EditableState } from '../product-summary-item/ProductSummaryItem';
import { SiteProductsSummary } from '../site-products-summary/SiteProductsSummary';

interface Props {
  organization: OrganizationListItem;
  updatedProducts?: ProductAllowance[];
  billingPeriod: BillingPeriod;
  currency: Currency;
  editable: EditableState;
  subscriptionEndDate?: string;
  onPlanChange?(product: ProductAllowance): void;
  showBundleDiscount: boolean;
}

const S = {
  Container: styled.div`
    border: 1px solid ${tokens.color.ui_border};
    border-radius: ${tokens.spacer.x0pt5};
    background-color: ${tokens.color.white};
  `,
  HeaderContainer: styled.div`
    display: flex;
    flex-flow: column;
    padding: ${tokens.spacer.x2};
  `,
  SiteContainer: styled.div`
    padding: ${tokens.spacer.x4} ${tokens.spacer.x2};
    border-top: 1px solid ${tokens.color.ui_border};
  `,
};

export function OrganizationSummary({
  organization,
  updatedProducts,
  billingPeriod,
  currency,
  editable,
  subscriptionEndDate,
  onPlanChange,
  showBundleDiscount,
}: Props): JSX.Element {
  const { t } = useTranslationWithPrefix('checkout:wizard.subscriptions');
  return (
    <S.Container>
      <S.HeaderContainer>
        <Text textType="caption" textColor="text_light">
          {t('organization')}
        </Text>
        <Text textType="subtitle2">{organization.name}</Text>
      </S.HeaderContainer>
      {organization.sites.map((site) => (
        <S.SiteContainer key={site.id}>
          <SiteProductsSummary
            site={site}
            currency={currency}
            onPlanChange={onPlanChange}
            billingPeriod={billingPeriod}
            updatedProducts={updatedProducts}
            editable={editable}
            subscriptionEndDate={subscriptionEndDate}
            showBundleDiscount={showBundleDiscount}
          />
        </S.SiteContainer>
      ))}
    </S.Container>
  );
}
